import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { VehicleAlert } from '../../hooks/useVehicleAlerts';
import { isCriticalAlert } from '../../monitoringUtils';
import './AlertsTab.scss';

type AlertCategory = 'components' | 'brake' | 'fluid' | 'battery' | 'others';

const CATEGORIES: { key: AlertCategory; icon: string; labelId: string }[] = [
    { key: 'components', icon: 'rioglyph-component-custom-recurrent', labelId: 'intl-msg:alerts.components' },
    { key: 'brake', icon: 'rioglyph-brake', labelId: 'intl-msg:alerts.brakes' },
    { key: 'fluid', icon: 'rioglyph-temperature', labelId: 'intl-msg:alerts.fluid' },
    { key: 'battery', icon: 'rioglyph-battery-level-full', labelId: 'intl-msg:alerts.battery' },
    { key: 'others', icon: 'rioglyph-exclamation', labelId: 'intl-msg:alerts.others' },
];

const KNOWN: AlertCategory[] = ['components', 'brake', 'fluid', 'battery'];

const categoryOf = (alert: VehicleAlert): AlertCategory => {
    const category = String(alert.category ?? '') as AlertCategory;
    return KNOWN.includes(category) ? category : 'others';
};

const value = (input: unknown) => (input === undefined || input === null || input === '' ? undefined : String(input));

interface IAlertsTabProps {
    alerts: VehicleAlert[];
    loading?: boolean;
}

const AlertsTab = ({ alerts, loading }: IAlertsTabProps) => {
    const intl = useIntl();
    const notAvailable = intl.formatMessage({ id: 'intl-msg:alerts.notAvailable' });
    const notInformed = intl.formatMessage({ id: 'intl-msg:alerts.notInformed' });
    const [category, setCategory] = useState<AlertCategory>('components');
    const [selected, setSelected] = useState<VehicleAlert | null>(null);

    const byCategory = useMemo(() => {
        const map = new Map<AlertCategory, VehicleAlert[]>(CATEGORIES.map(item => [item.key, []]));
        for (const alert of alerts) {
            map.get(categoryOf(alert))?.push(alert);
        }
        return map;
    }, [alerts]);

    useEffect(() => {
        setSelected(null);
        setCategory(alerts.length ? categoryOf(alerts[0]) : 'components');
    }, [alerts]);

    if (loading) {
        return (
            <ul className='fm-alertcards'>
                {['a1', 'a2', 'a3'].map(key => (
                    <li key={key}>
                        <span className='fm-skel' style={{ width: '100%', height: 72 }} />
                    </li>
                ))}
            </ul>
        );
    }

    if (selected) {
        const rows: [string, string | undefined][] = [
            ['SA', value(selected.sourceAddressDescription) ?? value(selected.sourceAddress) ?? notInformed],
            [
                intl.formatMessage({ id: 'intl-msg:alerts.spnDescription' }),
                value(selected.spnDescription) ?? notAvailable,
            ],
            [
                intl.formatMessage({ id: 'intl-msg:alerts.fmiDescription' }),
                value(selected.fmiDescription) ?? notAvailable,
            ],
            [intl.formatMessage({ id: 'intl-msg:alerts.fmiCode' }), value(selected.fmi) ?? notAvailable],
            [intl.formatMessage({ id: 'intl-msg:alerts.spnCode' }), value(selected.spn) ?? notAvailable],
            [intl.formatMessage({ id: 'intl-msg:alerts.dateTime' }), value(selected.date) ?? notAvailable],
            [intl.formatMessage({ id: 'intl-msg:alerts.description' }), value(selected.description) ?? notAvailable],
            [
                intl.formatMessage({ id: 'intl-msg:alerts.status' }),
                value(selected.lampStatus) ?? value(selected.status) ?? notInformed,
            ],
        ];
        if (value(selected.km)) {
            rows.push([intl.formatMessage({ id: 'intl-msg:alerts.odometer' }), `${value(selected.km)} km`]);
        }
        return (
            <>
                <button type='button' className='fm-linkbtn fm-back' onClick={() => setSelected(null)}>
                    <span className='rioglyph rioglyph-arrow-left' />{' '}
                    {intl.formatMessage({ id: 'intl-msg:alerts.details' })}
                </button>
                <dl className='fm-dmeta fm-dmeta-spaced'>
                    {rows.map(([label, text]) => (
                        <div key={label} className='fm-dmeta-row'>
                            <dt>{label}</dt>
                            <dd>{text}</dd>
                        </div>
                    ))}
                </dl>
            </>
        );
    }

    const list = byCategory.get(category) ?? [];
    const current = CATEGORIES.find(item => item.key === category);

    return (
        <>
            <div className='fm-segtabs' role='tablist'>
                {CATEGORIES.map(item => {
                    const count = byCategory.get(item.key)?.length ?? 0;
                    return (
                        <button
                            key={item.key}
                            type='button'
                            role='tab'
                            aria-selected={category === item.key}
                            title={intl.formatMessage({ id: item.labelId })}
                            className='fm-segtab'
                            onClick={() => setCategory(item.key)}
                        >
                            <span className={`rioglyph ${item.icon}`} />
                            {count > 0 && <span className='fm-segtab__dot' />}
                        </button>
                    );
                })}
            </div>

            <h3 className='fm-section-title'>{current ? intl.formatMessage({ id: current.labelId }) : ''}</h3>

            {list.length ? (
                <ul className='fm-alertcards'>
                    {list.map((alert, index) => {
                        const critical = isCriticalAlert(alert);
                        return (
                            <li key={String(alert.id ?? `${alert.spn}-${alert.fmi}-${alert.date}-${index}`)}>
                                <button
                                    type='button'
                                    className={`fm-alertcard${critical ? ' fm-critical' : ''}`}
                                    onClick={() => setSelected(alert)}
                                >
                                    <span className='rioglyph rioglyph-warning-sign fm-alertcard__icon' />
                                    <span className='fm-alertcard__body'>
                                        <b>
                                            {value(alert.spnDescription) ??
                                                value(alert.description) ??
                                                value(alert.sourceAddress) ??
                                                intl.formatMessage({ id: 'intl-msg:alerts.noDescription' })}
                                        </b>
                                        <small>
                                            SA:{' '}
                                            {value(alert.sourceAddressDescription) ??
                                                value(alert.sourceAddress) ??
                                                notAvailable}{' '}
                                            | SPN: {value(alert.spn) ?? notAvailable} | FMI:{' '}
                                            {value(alert.fmi) ?? notAvailable}
                                        </small>
                                        <small>{value(alert.fmiDescription) ?? notAvailable}</small>
                                        <small>{value(alert.date) ?? notAvailable}</small>
                                    </span>
                                    <span className='rioglyph rioglyph-chevron-right fm-alertcard__chevron' />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <p className='fm-muted'>{intl.formatMessage({ id: 'intl-msg:alerts.noAlerts' })}</p>
            )}
        </>
    );
};

export default AlertsTab;
